import { useAuthStore, type User } from './useAuth';

const KNOWN_WAREHOUSE_PLANS = new Set([
    'starter',
    'operations',
    'professional',
    'enterprise',
    'free_trial',
]);

/**
 * Resolves the effective subscription tier (`'none' | 'starter' | 'operations'`) for the
 * current authenticated frontend user session, mirroring server-side `resolveWarehouseEffectiveTier`.
 */
export function resolveUserEffectiveTier(
    user: User | null | undefined
): 'none' | 'starter' | 'operations' {
    if (!user) return 'none';
    if (user.role === 'carrier') return 'none';
    if (user.role === 'admin') {
        return user.isAdmin === true ? 'operations' : 'none';
    }

    if (
        user.effectiveTier === 'operations' ||
        user.effectiveTier === 'starter' ||
        user.effectiveTier === 'none'
    ) {
        return user.effectiveTier;
    }

    if (!user.warehouseId) {
        return 'none';
    }

    const normalizedStatus =
        typeof user.trialStatus === 'string' && user.trialStatus.trim().length > 0
            ? user.trialStatus.trim().toLowerCase()
            : null;
    if (
        normalizedStatus &&
        normalizedStatus !== 'trialing' &&
        normalizedStatus !== 'active'
    ) {
        return 'none';
    }

    const normalizedPlan =
        user.plan === undefined || user.plan === null
            ? null
            : typeof user.plan === 'string'
              ? user.plan.trim().toLowerCase() || null
              : '__malformed__';

    if (normalizedStatus === 'trialing') {
        if ((user as any).trialEndAt !== undefined && (user as any).trialEndAt !== null) {
            const parsedEnd = new Date((user as any).trialEndAt);
            if (Number.isNaN(parsedEnd.getTime()) || parsedEnd <= new Date()) {
                return 'none';
            }
        }
        if (normalizedPlan !== null && !KNOWN_WAREHOUSE_PLANS.has(normalizedPlan)) {
            return 'none';
        }
        return 'operations';
    }

    if (normalizedPlan === 'starter') {
        return 'starter';
    }

    if (
        normalizedPlan !== null &&
        normalizedPlan !== 'operations' &&
        normalizedPlan !== 'professional' &&
        normalizedPlan !== 'enterprise' &&
        normalizedPlan !== 'free_trial'
    ) {
        return 'none';
    }

    if (normalizedStatus === 'active' && normalizedPlan === null) {
        return 'none';
    }

    return 'operations';
}

/**
 * Evaluates whether the current authenticated user is entitled to Operations-tier
 * ($199 USD/month per facility or active 30-day full-access trial) features on the frontend.
 *
 * Prevents Starter ($99 USD/month) and unentitled sessions from firing background
 * requests to `/api/agent/*` and `/api/ai/*`. Server-side enforcement remains authoritative
 * via `requireOperationsTier` and `resolveWarehouseEffectiveTier`.
 */
export function isOperationsEntitledUser(user: User | null | undefined): boolean {
    return resolveUserEffectiveTier(user) === 'operations';
}

export function useWarehouseEntitlement() {
    const { user } = useAuthStore();
    const effectiveTier = resolveUserEffectiveTier(user);
    const isOperationsEntitled = effectiveTier === 'operations';

    return {
        effectiveTier,
        isOperationsEntitled,
        isStarter: effectiveTier === 'starter',
        currentPlan: user?.plan ?? null,
    };
}
